import { useEffect, useMemo, useState } from "react";
import { tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { navigate } from "../lib/router";
import { colorFor, STATUS } from '../lib/definitions/palette';
import { DECODE_HEALTH_COLOR, decodeHealthSeverity } from '../lib/definitions/health';
import { formatNumber, relativeTime } from "../lib/format";
import { MqttBrokerTable } from "../components/MqttBrokerTable";
import type { MqttBroker, MqttGateway, MqttTopic, MqttUndecryptableRecord, MqttUndecryptableResponse } from "../api/types";

// Scoping this project: all of the things MeshMonitor offers — the MQTT
// viewer, the sim nodes, all of it — were wanted here too. Phase
// 3's backend shipped and was called complete, but this UI sat behind a
// stub ("isn't built in this frontend yet") until now — a named
// requirement, not polish. Built directly against the running backend's
// real response shapes (pulled live, 2026-09-20, not a written
// spec): GET /api/mqtt/brokers, /api/mqtt/gateways, and /api/mqtt/topics
// each return a bare ARRAY; GET /api/mqtt/undecryptable returns an OBJECT
// ({by_reason, recent}). That inconsistency is real and left as-is below,
// per instruction not to smooth it over.
//
// What this page leads with, and why: the single most useful fact in this
// data isn't any one broker/gateway/topic row, it's the cross-reference —
// a channel with high message volume and a high proportion of
// undecryptable samples almost certainly means a missing channel PSK. The
// backend doesn't hand back that cross-reference as one field (topics and
// undecryptable-log are two separate endpoints), so this page computes it
// client-side from the two arrays it already has and puts it first, as one
// table, rather than making someone flip between four tables to notice it
// themselves. lib/definitions.ts's decodeHealthSeverity() is graded
// (ok/warning/critical) rather than a flat "this is broken" claim, since
// /api/mqtt/undecryptable's `recent` list is capped at the newest 100
// records, not a true lifetime total per channel.

function parseTopicChannel(topic: string): string {
  // Meshtastic's own MQTT topic convention: .../e/<channel>/<gateway> (or
  // .../c/... for a still-encrypted variant, .../json/... for the JSON
  // bridge) — the segment right after the mode marker is the channel name.
  // Falls back to the raw topic string for anything that doesn't match
  // rather than guessing wrong.
  const parts = topic.split("/");
  const idx = parts.findIndex((p) => p === "e" || p === "c" || p === "json");
  if (idx >= 0 && idx + 1 < parts.length) return parts[idx + 1];
  return topic;
}

interface ChannelRow {
  brokerId: string;
  channel: string;
  messageCount: number;
  lastSeenAt: string | null;
  undecryptableSample: number;
}

function buildChannelRows(topics: MqttTopic[], recent: MqttUndecryptableRecord[]): ChannelRow[] {
  const map = new Map<string, ChannelRow>();
  const key = (broker: string, channel: string) => `${broker}::${channel}`;
  for (const t of topics) {
    const channel = parseTopicChannel(t.topic);
    const k = key(t.broker_id, channel);
    const row = map.get(k);
    if (row) {
      row.messageCount += t.message_count;
      if (!row.lastSeenAt || t.last_seen_at > row.lastSeenAt) row.lastSeenAt = t.last_seen_at;
    } else {
      map.set(k, { brokerId: t.broker_id, channel, messageCount: t.message_count, lastSeenAt: t.last_seen_at, undecryptableSample: 0 });
    }
  }
  for (const r of recent) {
    const k = key(r.broker_id, r.channel_name);
    const row = map.get(k);
    if (row) row.undecryptableSample += 1;
    else map.set(k, { brokerId: r.broker_id, channel: r.channel_name, messageCount: 0, lastSeenAt: null, undecryptableSample: 1 });
  }
  return Array.from(map.values()).sort((a, b) => b.undecryptableSample - a.undecryptableSample || b.messageCount - a.messageCount);
}

function humanizeReason(reason: string): string {
  const s = reason.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Meshtastic/MeshCore node ids ("!00000001") are how gateway_id/from_id
// show up here — the same id shape the node roster and #/nodes/<id> route
// already use, so these are clickable the same way a packet's from/to node
// or an audit finding's subject is elsewhere in the app.
function NodeLink({ id }: { id: string | null | undefined }) {
  if (!id) return <>--</>;
  return (
    <span className="mqtt-node-link" onClick={() => navigate(`#/nodes/${encodeURIComponent(id)}`)}>
      {id}
    </span>
  );
}

type Loadable<T> = T | null | "unavailable";

export function MqttPage() {
  const { effectiveTheme } = useAppData();
  const [brokers, setBrokers] = useState<Loadable<MqttBroker[]>>(null);
  const [gateways, setGateways] = useState<Loadable<MqttGateway[]>>(null);
  const [topics, setTopics] = useState<Loadable<MqttTopic[]>>(null);
  const [undecryptable, setUndecryptable] = useState<Loadable<MqttUndecryptableResponse>>(null);

  useEffect(() => {
    let cancelled = false;
    function poll() {
      tryApiGet<MqttBroker[]>("/api/mqtt/brokers")
        .then((r) => !cancelled && setBrokers(r ?? "unavailable"))
        .catch(() => !cancelled && setBrokers("unavailable"));
      tryApiGet<MqttGateway[]>("/api/mqtt/gateways")
        .then((r) => !cancelled && setGateways(r ?? "unavailable"))
        .catch(() => !cancelled && setGateways("unavailable"));
      tryApiGet<MqttTopic[]>("/api/mqtt/topics")
        .then((r) => !cancelled && setTopics(r ?? "unavailable"))
        .catch(() => !cancelled && setTopics("unavailable"));
      tryApiGet<MqttUndecryptableResponse>("/api/mqtt/undecryptable")
        .then((r) => !cancelled && setUndecryptable(r ?? "unavailable"))
        .catch(() => !cancelled && setUndecryptable("unavailable"));
    }
    poll();
    const id = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const brokerLabel = useMemo(() => {
    const m = new Map<string, string>();
    if (Array.isArray(brokers)) for (const b of brokers) m.set(b.id, b.label);
    return m;
  }, [brokers]);

  const channelRows = useMemo(() => {
    if (!Array.isArray(topics)) return [];
    const recent = undecryptable && undecryptable !== "unavailable" ? undecryptable.recent : [];
    return buildChannelRows(topics, recent);
  }, [topics, undecryptable]);

  const recentSampleTotal = undecryptable && undecryptable !== "unavailable" ? undecryptable.recent.length : 0;

  return (
    <div className="node-list-page">
      <h2 className="audit-page-title">MQTT</h2>

      {/* --- Lead section: channel decode health, the cross-referenced signal --- */}
      <section className="page-section">
        <div className="page-section-title">Channel decode health</div>
        {topics === null && <div className="node-detail-note">Loading…</div>}
        {topics === "unavailable" && (
          <div className="node-detail-note">MQTT topics data didn't respond as expected — MQTT ingest may still be starting up.</div>
        )}
        {Array.isArray(topics) &&
          (channelRows.length === 0 ? (
            <div className="empty-state">No MQTT channel traffic seen yet.</div>
          ) : (
            <div className="node-list-table-wrap">
              <table className="node-list-table">
                <thead>
                  <tr>
                    <th>Channel</th>
                    <th>Broker</th>
                    <th>Messages</th>
                    <th>Share of recent decode failures</th>
                    <th>Decode health</th>
                  </tr>
                </thead>
                <tbody>
                  {channelRows.map((r) => {
                    // Severity is this channel's SHARE of the shared
                    // recent-undecryptable sample window (capped at 100
                    // system-wide, across every channel/broker) — not this
                    // channel's failures against its own lifetime message
                    // count, which would understate a channel that has
                    // ALWAYS failed once its volume passes the shared
                    // window size. A channel accounting for all (or nearly
                    // all) of the currently-failing traffic is the "likely
                    // missing key" signal, regardless of its total volume.
                    const severity = decodeHealthSeverity(r.undecryptableSample, recentSampleTotal);
                    const label = severity === "critical" ? "Likely missing channel key" : severity === "warning" ? "Some failures" : "Decoding";
                    return (
                      <tr key={`${r.brokerId}::${r.channel}`}>
                        <td>{r.channel}</td>
                        <td>{brokerLabel.get(r.brokerId) || r.brokerId}</td>
                        <td>{formatNumber(r.messageCount)}</td>
                        <td>{r.undecryptableSample > 0 ? `${formatNumber(r.undecryptableSample)} of ${formatNumber(recentSampleTotal)}` : "0"}</td>
                        <td>
                          <span className="badge" style={{ background: colorFor(DECODE_HEALTH_COLOR[severity], effectiveTheme) }}>
                            {label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
      </section>

      {/* --- Brokers --- */}
      <section className="page-section">
        <div className="page-section-title">Brokers</div>
        <MqttBrokerTable brokers={brokers} />
      </section>

      {/* --- Gateways --- */}
      <section className="page-section">
        <div className="page-section-title">Gateways heard</div>
        {gateways === null && <div className="node-detail-note">Loading…</div>}
        {gateways === "unavailable" && <div className="node-detail-note">MQTT gateway data didn't respond as expected.</div>}
        {Array.isArray(gateways) &&
          (gateways.length === 0 ? (
            <div className="empty-state">No gateway nodes heard through any broker yet.</div>
          ) : (
            <div className="node-list-table-wrap">
              <table className="node-list-table">
                <thead>
                  <tr>
                    <th>Gateway</th>
                    <th>Broker</th>
                    <th>Packets</th>
                    <th>First seen</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {gateways.map((g, i) => (
                    <tr key={`${g.broker_id}-${g.gateway_id}-${i}`}>
                      <td>
                        <NodeLink id={g.gateway_id} />
                      </td>
                      <td>{brokerLabel.get(g.broker_id) || g.broker_id}</td>
                      <td>{formatNumber(g.packet_count)}</td>
                      <td>{relativeTime(g.first_seen_at)}</td>
                      <td>{relativeTime(g.last_seen_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </section>

      {/* --- Undecryptable log --- */}
      <section className="page-section">
        <div className="page-section-title">Undecryptable messages</div>
        {undecryptable === null && <div className="node-detail-note">Loading…</div>}
        {undecryptable === "unavailable" && <div className="node-detail-note">MQTT undecryptable-message data didn't respond as expected.</div>}
        {undecryptable && undecryptable !== "unavailable" && (
          <>
            <div className="node-detail-note">
              Counts here come from the persisted undecryptable-message log, which can differ from each broker's own rolling
              counters above (those can reset on reconnect; this log doesn't).
            </div>
            {undecryptable.by_reason.length > 0 && (
              <div className="audit-counts">
                {undecryptable.by_reason.map((r) => (
                  <span key={r.reason} className="badge" style={{ background: colorFor(STATUS.critical, effectiveTheme) }}>
                    {humanizeReason(r.reason)}: {formatNumber(r.count)}
                  </span>
                ))}
              </div>
            )}
            {undecryptable.recent.length === 0 ? (
              <div className="empty-state">No undecryptable messages logged.</div>
            ) : (
              <div className="node-list-table-wrap">
                <table className="node-list-table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th>Broker</th>
                      <th>Gateway</th>
                      <th>From</th>
                      <th>Reason</th>
                      <th>Observed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {undecryptable.recent.map((r) => (
                      <tr key={r.id}>
                        <td>{r.channel_name}</td>
                        <td>{brokerLabel.get(r.broker_id) || r.broker_id}</td>
                        <td>
                          <NodeLink id={r.gateway_id} />
                        </td>
                        <td>
                          <NodeLink id={r.from_id} />
                        </td>
                        <td>{humanizeReason(r.reason)}</td>
                        <td>{relativeTime(r.observed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="node-detail-note" style={{ margin: "8px 0 0", borderStyle: "solid" }}>
                  Showing the {formatNumber(undecryptable.recent.length)} most recent only.
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
